import type { Option } from "@sorcery/db";
import { Check, ChevronDown, type LucideIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { cn } from "../../../lib/utils";
import { Input } from "../../atoms/Input";

import type { IconType } from "react-icons";
import { LoadingSpinner } from "../../atoms/LoadingSpinner";
import { Command, CommandEmpty, CommandItem, CommandList, CommandLoading } from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";

export const AsyncCombobox = ({
  query,
  setQuery,
  options,
  onSelect,
  arrow,
  autoFocus,
  clearAfterSelect,
  placeholder,
  loading,
  icon,
  highlightType,
}: {
  query: string;
  setQuery: (query: string) => void;
  options: Option[];
  onSelect: (v: Option) => void;
  arrow?: boolean;
  autoFocus?: boolean;
  clearAfterSelect?: boolean;
  placeholder?: string;
  loading?: boolean;
  icon?: IconType | LucideIcon;
  highlightType?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option>();

  // manage ref to input so we can refocus it when the popover closes due to item selection
  const ref = useRef<HTMLInputElement>(null);

  // manage internal query state locally so we can set the input to a controlled value and split what the user sees from the query value which is debounced upstream
  const [internalQuery, setInternalQuery] = useState(query);

  useEffect(() => {
    setInternalQuery(query);
  }, [query]);
  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
    }
  }, [autoFocus]);

  const filteredOptions =
    query === ""
      ? options
      : options.filter((o) => {
          return o.label
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""));
        });

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger
        asChild
        onClick={(e) => {
          // force open whenever interacting with the input
          if (internalQuery.length > 0) {
            setOpen(true);
          }
          // stop propagation to stop the default trigger behavior toggling the input closed as it expects
          // the trigger to behave like a button, but we have opted for an input
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <div className="flex items-center [&[data-state=open]>div>svg]:rotate-180 relative">
          <div className="w-full -mb-2">
            <Input
              ref={ref}
              value={internalQuery}
              onChange={(v) => {
                setInternalQuery(v);
                setSelected(undefined);
                // force open whenever interacting with the input unless clearing
                v.length === 0 ? setOpen(false) : setOpen(true);
                // send upstream to the query change handler to fetch new data
                setQuery(v);
              }}
              LeftComponent={icon ?? (selected ? Check : BiSearch)}
              placeholder={placeholder ?? "Start typing..."}
            />
          </div>
          {arrow && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDown className="h-4 w-4 transition-all" />
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command>
          <CommandList>
            {loading && (
              <CommandLoading className="flex py-2 px-4">
                <LoadingSpinner size={18} />
              </CommandLoading>
            )}

            {!loading && !filteredOptions.length && query && (
              <CommandEmpty>No results.</CommandEmpty>
            )}

            {filteredOptions.map((o) => (
              <CommandItem
                key={o.label}
                value={o.value}
                onSelect={() => {
                  onSelect(o);
                  setOpen(false);
                  if (clearAfterSelect) {
                    setInternalQuery("");
                    setSelected(undefined);
                    ref.current?.focus();
                    // send upstream
                    setQuery("");
                  } else {
                    setSelected(o);
                    // send upstream
                    setQuery(o.label);
                  }
                }}
                className={cn(
                  selected?.value === o.value && "bg-slate-100 text-slate-900",
                  highlightType && o.type === "card" && "hover:!bg-primary-light hover:!text-white",
                  highlightType && o.type === "deck" && "hover:!bg-info hover:!text-white",
                  highlightType && o.type === "user" && "hover:!bg-black hover:!text-white",
                )}
              >
                <span className="w-full flex justify-between items-center">
                  <span className="line-clamp-1">{o.label}</span>
                  {highlightType && o.type && (
                    <span className={"text-sm font-semibold capitalize"}>{o.type}</span>
                  )}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
